import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Ban, Building2, Mail, RotateCw, Send, ShieldCheck, Smartphone } from 'lucide-react';
import { Button } from '../components';
import {
  ADHOC_CATEGORIEEN,
  datumLabel,
  duurLabel,
  getAdhocUren,
  getAdhocWerkgevers,
  registreerAdhocUren,
  trekAdhocIn,
  verstuurAdhocOpnieuw,
  type AdhocKanaal,
  type AdhocRegistratie,
} from '../services/adhocUrenApi';

/**
 * Ad-hoc uren buiten de agenda om (migratie 163). Twee routes:
 *  - SMS:    medewerker-opvolging; de medewerker accordeert anoniem via een
 *            SMS-link. HR ziet niets, de factuur toont alleen een transactie-ID.
 *  - E-mail: interne afspraak; een zakelijke contactpersoon van de opdrachtgever
 *            accordeert via een e-maillink. Alleen het eigen domein van de
 *            opdrachtgever wordt geaccepteerd.
 */
const QK = ['professional-adhoc-uren'];
const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis');
const vandaag = () => new Date().toLocaleDateString('sv-SE');
const dagenTerug = (n: number) => new Date(Date.now() - n * 86_400_000).toLocaleDateString('sv-SE');
const euro = (v: string | number) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(typeof v === 'string' ? parseFloat(v) : v);
const DUUR_OPTIES = Array.from({ length: 32 }, (_, i) => (i + 1) * 15);

const STATUS: Record<string, { label: string; klasse: string }> = {
  PENDING_CLIENT_CONFIRMATION: { label: 'Wacht op medewerker', klasse: 'bg-amber-100 text-amber-800' },
  PENDING_EMAIL_APPROVAL: { label: 'Wacht op contactpersoon', klasse: 'bg-amber-100 text-amber-800' },
  APPROVED_RELEASED: { label: 'Geaccordeerd', klasse: 'bg-green-100 text-green-800' },
  INVOICED: { label: 'Gefactureerd', klasse: 'bg-blue-100 text-blue-800' },
  REJECTED: { label: 'Afgewezen', klasse: 'bg-red-100 text-red-800' },
  WITHDRAWN: { label: 'Ingetrokken', klasse: 'bg-gray-100 text-gray-600' },
};
const isOpen = (r: AdhocRegistratie) => r.status === 'PENDING_CLIENT_CONFIRMATION' || r.status === 'PENDING_EMAIL_APPROVAL';

const leegFormulier = {
  employer_id: '',
  kanaal: 'SMS' as AdhocKanaal,
  categorie: '',
  datum: vandaag(),
  tijd_minuten: 30,
  toelichting: '',
  telefoon: '',
  contact_email: '',
};

const ProfessionalAdhocUrenPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: registraties = [], isLoading } = useQuery({ queryKey: QK, queryFn: getAdhocUren });
  const { data: werkgevers = [] } = useQuery({ queryKey: ['professional-adhoc-werkgevers'], queryFn: getAdhocWerkgevers });
  const [f, setF] = useState(leegFormulier);
  const zet = <K extends keyof typeof leegFormulier>(k: K, v: (typeof leegFormulier)[K]) => setF((x) => ({ ...x, [k]: v }));

  const werkgever = werkgevers.find((w) => w.employer_id === f.employer_id);
  const domein = werkgever?.zakelijk_domein ?? null;

  // Directe feedback; de backend doet dezelfde check (plus freemail) en is leidend.
  const domeinFout = useMemo(() => {
    if (f.kanaal !== 'EMAIL' || !f.contact_email.includes('@')) return null;
    const ingevoerd = f.contact_email.trim().toLowerCase().split('@').pop();
    if (!domein) return 'Voor deze opdrachtgever is geen geverifieerd zakelijk domein bekend.';
    return ingevoerd === domein ? null : `Alleen adressen eindigend op @${domein} zijn toegestaan.`;
  }, [f.kanaal, f.contact_email, domein]);

  const verversen = () => qc.invalidateQueries({ queryKey: QK });
  const registreer = useMutation({
    mutationFn: () =>
      registreerAdhocUren({
        employer_id: f.employer_id,
        kanaal: f.kanaal,
        categorie: f.categorie,
        datum: f.datum,
        tijd_minuten: f.tijd_minuten,
        toelichting: f.toelichting.trim() || undefined,
        ...(f.kanaal === 'SMS' ? { telefoon: f.telefoon } : { contact_email: f.contact_email.trim() }),
      }),
    onSuccess: () => {
      toast.success(f.kanaal === 'SMS' ? 'Uren vastgelegd — de medewerker krijgt een SMS om te bevestigen' : 'Uren vastgelegd — de contactpersoon krijgt een e-mail om te accorderen');
      setF({ ...leegFormulier, employer_id: f.employer_id, kanaal: f.kanaal });
      verversen();
    },
    onError: foutmelding,
  });
  const intrekken = useMutation({
    mutationFn: trekAdhocIn,
    onSuccess: () => {
      toast.success('Verzoek ingetrokken');
      verversen();
    },
    onError: foutmelding,
  });
  const opnieuw = useMutation({
    mutationFn: ({ id, telefoon }: { id: string; telefoon?: string }) => verstuurAdhocOpnieuw(id, telefoon),
    onSuccess: () => {
      toast.success('Nieuwe link verstuurd');
      verversen();
    },
    onError: foutmelding,
  });

  const kanVersturen =
    f.employer_id &&
    f.categorie &&
    f.datum &&
    (f.kanaal === 'SMS' ? f.telefoon.trim().length >= 10 : f.contact_email.includes('@') && !domeinFout);

  const routeKaart = (kanaal: AdhocKanaal, Icoon: typeof Smartphone, titel: string, uitleg: string) => (
    <button
      type="button"
      onClick={() => setF((x) => ({ ...x, kanaal, categorie: '' }))}
      aria-pressed={f.kanaal === kanaal}
      className={`flex-1 text-left rounded-lg border p-3 transition ${
        f.kanaal === kanaal ? 'border-[#0B1F3A] ring-1 ring-[#0B1F3A] bg-blue-50/50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <span className="flex items-center gap-2 font-medium text-gray-900"><Icoon className="w-4 h-4" aria-hidden />{titel}</span>
      <span className="mt-1 block text-xs text-gray-600">{uitleg}</span>
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div>
        <Link to="/professional/hours" className="inline-flex items-center text-sm text-blue-600 hover:underline mb-1"><ArrowLeft className="w-4 h-4 mr-1" />Urenregistratie</Link>
        <h1 className="text-2xl font-bold text-gray-900">Ad-hoc uren buiten de agenda</h1>
        <p className="text-sm text-gray-600 max-w-3xl">
          Voor onverwachte uren die niet in de agenda stonden. Ze tellen pas mee voor de maandelijkse verzamelfactuur
          nadat de medewerker of de contactpersoon ze heeft bevestigd.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Gesprek op de werkvloer zonder afspraak vooraf?{' '}
          <Link to="/professional/adhoc-melding" className="text-blue-600 hover:underline">Ad-hoc melding registreren</Link>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-6 items-start">
        <form
          className="bg-white rounded-lg border border-gray-200 p-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (kanVersturen) registreer.mutate();
          }}
        >
          <h2 className="font-semibold text-gray-900">Nieuwe registratie</h2>

          <label className="block text-sm">
            <span className="text-gray-700">Opdrachtgever</span>
            <select className="mt-1 w-full rounded-md border-gray-300" value={f.employer_id} onChange={(e) => zet('employer_id', e.target.value)} required>
              <option value="">Kies…</option>
              {werkgevers.map((w) => <option key={w.employer_id} value={w.employer_id}>{w.organisatie}</option>)}
            </select>
          </label>

          <fieldset>
            <legend className="text-sm text-gray-700 mb-1">Wie accordeert?</legend>
            <div className="flex flex-col sm:flex-row gap-2">
              {routeKaart('SMS', Smartphone, 'Medewerker via SMS', 'Opvolging met of voor een medewerker. HR ziet niets.')}
              {routeKaart('EMAIL', Mail, 'Contactpersoon via e-mail', 'Intern overleg of workshop, buiten HR om.')}
            </div>
          </fieldset>

          <label className="block text-sm">
            <span className="text-gray-700">Situatie</span>
            <select className="mt-1 w-full rounded-md border-gray-300" value={f.categorie} onChange={(e) => zet('categorie', e.target.value)} required>
              <option value="">Kies…</option>
              {Object.entries(ADHOC_CATEGORIEEN[f.kanaal]).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">Datum</span>
              <input type="date" className="mt-1 w-full rounded-md border-gray-300" value={f.datum} min={dagenTerug(60)} max={vandaag()} onChange={(e) => zet('datum', e.target.value)} required />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">Duur</span>
              <select className="mt-1 w-full rounded-md border-gray-300" value={f.tijd_minuten} onChange={(e) => zet('tijd_minuten', Number(e.target.value))}>
                {DUUR_OPTIES.map((m) => <option key={m} value={m}>{duurLabel(m)}</option>)}
              </select>
            </label>
          </div>

          {f.kanaal === 'SMS' ? (
            <label className="block text-sm">
              <span className="text-gray-700">Mobiel nummer van de medewerker</span>
              <input type="tel" inputMode="tel" autoComplete="off" placeholder="06 12345678" className="mt-1 w-full rounded-md border-gray-300" value={f.telefoon} onChange={(e) => zet('telefoon', e.target.value)} required />
              <span className="mt-1 flex gap-1 text-xs text-gray-500"><ShieldCheck className="w-3.5 h-3.5 shrink-0" aria-hidden />Het nummer wordt alleen gebruikt voor de SMS en niet opgeslagen.</span>
            </label>
          ) : (
            <label className="block text-sm">
              <span className="text-gray-700">Zakelijk e-mailadres contactpersoon</span>
              <input
                type="email"
                autoComplete="off"
                placeholder={domein ? `naam@${domein}` : 'naam@bedrijf.nl'}
                className={`mt-1 w-full rounded-md ${domeinFout ? 'border-red-400' : 'border-gray-300'}`}
                value={f.contact_email}
                onChange={(e) => zet('contact_email', e.target.value)}
                aria-invalid={Boolean(domeinFout)}
                required
              />
              {domeinFout ? (
                <span className="mt-1 block text-xs text-red-600" role="alert">{domeinFout}</span>
              ) : (
                werkgever && <span className="mt-1 flex gap-1 text-xs text-gray-500"><Building2 className="w-3.5 h-3.5 shrink-0" aria-hidden />{domein ? `Alleen @${domein} — privé-adressen worden geweigerd.` : 'Geen zakelijk domein bekend: deze route is nog niet beschikbaar.'}</span>
              )}
            </label>
          )}

          <label className="block text-sm">
            <span className="text-gray-700">Toelichting <span className="text-gray-400">(optioneel, ziet alleen de accordeerder)</span></span>
            <textarea className="mt-1 w-full rounded-md border-gray-300" rows={2} maxLength={500} value={f.toelichting} onChange={(e) => zet('toelichting', e.target.value)} />
          </label>

          <Button type="submit" className="w-full" disabled={!kanVersturen} loading={registreer.isPending}>
            <Send className="w-4 h-4 mr-2" />
            {f.kanaal === 'SMS' ? 'Vastleggen & SMS versturen' : 'Vastleggen & e-mail versturen'}
          </Button>
        </form>

        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 px-3 font-medium">Datum</th>
                <th className="py-2 px-3 font-medium">Opdrachtgever / situatie</th>
                <th className="py-2 px-3 font-medium">Accordeerder</th>
                <th className="py-2 px-3 font-medium text-right">Duur</th>
                <th className="py-2 px-3 font-medium text-right">Bedrag</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="py-10 text-center text-gray-500">Laden…</td></tr>}
              {!isLoading && registraties.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-gray-500">Nog geen ad-hoc uren geregistreerd.</td></tr>
              )}
              {registraties.map((r) => {
                const s = STATUS[r.status] ?? { label: r.status, klasse: 'bg-gray-100 text-gray-600' };
                return (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0 align-top">
                    <td className="py-2 px-3 whitespace-nowrap">{datumLabel(r.datum)}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium text-gray-900">{r.organisatie}</div>
                      <div className="text-xs text-gray-500">{r.categorie_label}</div>
                      <div className="font-mono text-[11px] text-gray-400">{r.transactie_id}</div>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-600 whitespace-nowrap">
                      {r.kanaal === 'SMS' ? <><Smartphone className="inline w-3.5 h-3.5 mr-1" aria-hidden />{r.telefoon_hint}</> : <><Mail className="inline w-3.5 h-3.5 mr-1" aria-hidden />{r.contact_email}</>}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">{duurLabel(r.tijd_minuten)}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">{euro(r.bedrag)}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${r.verlopen ? 'bg-gray-100 text-gray-600' : s.klasse}`}>{r.verlopen ? 'Link verlopen' : s.label}</span>
                      {r.status === 'REJECTED' && r.afwijzing_reden && <div className="mt-1 text-xs text-red-700">“{r.afwijzing_reden}”</div>}
                    </td>
                    <td className="py-2 px-3">
                      {isOpen(r) && (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            title="Nieuwe link versturen"
                            onClick={() => {
                              if (r.kanaal === 'SMS') {
                                const tel = window.prompt(`Mobiel nummer van de medewerker opnieuw invoeren (eindigt op ${r.telefoon_hint?.slice(-3)})`);
                                if (tel) opnieuw.mutate({ id: r.id, telefoon: tel });
                              } else {
                                opnieuw.mutate({ id: r.id });
                              }
                            }}
                          >
                            <RotateCw className="w-4 h-4" aria-label="Opnieuw versturen" />
                          </Button>
                          <Button size="sm" variant="outline" title="Intrekken" onClick={() => window.confirm('Dit verzoek intrekken?') && intrekken.mutate(r.id)}>
                            <Ban className="w-4 h-4" aria-label="Intrekken" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalAdhocUrenPage;
